import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog } from "@/components/ui/Dialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { SubmitForm } from "@/components/submit/SubmitForm";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { boardDetailQueryOptions } from "@/hooks/useBoard";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { CellValue, UUID } from "@/lib/types";

/**
 * Spawn a new Folgeticket from a Mainticket. Reuses SubmitForm (internal variant)
 * so the new ticket honors the board's required fields, then routes the post to the
 * `/rows/{main}/follow-ups` endpoint via `submitFn` — which creates the ticket AND
 * the follow_up link in one call. Re-assign to a colleague via the Verantwortlich
 * field. The board detail (raw columns) is read from the already-cached board query.
 */
export function CreateFollowUpDialog({
  open,
  onOpenChange,
  mainRowId,
  boardId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mainRowId: UUID;
  boardId: UUID;
  onCreated?: () => void;
}) {
  const { user } = useAuth();
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Shares the board's own query definition: this dialog is mounted (closed) inside
  // every ticket panel, and a private single-page queryFn on the same key hijacked the
  // board's post-action refetch — see boardDetailQueryOptions.
  const detailQ = useQuery({ ...boardDetailQueryOptions(boardId), enabled: open });
  const detail = detailQ.data ?? null;

  function requestClose(next: boolean) {
    if (!next && dirty) {
      setConfirmOpen(true);
      return;
    }
    setDirty(false);
    onOpenChange(next);
  }

  function discardAndClose() {
    setConfirmOpen(false);
    setDirty(false);
    onOpenChange(false);
  }

  // The follow-up endpoint is keyed by the MAIN row, not the board — ignore the
  // boardId SubmitForm passes and post to /rows/{main}/follow-ups.
  const submitFollowUp = (_boardId: UUID, payload: { cells: Record<UUID, CellValue> }) =>
    api.createFollowUp(mainRowId, { cells: payload.cells });

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={requestClose}
        title={t.followUpDialogTitle}
        description={t.followUpDialogDescription}
        width={560}
      >
        {detail ? (
          <SubmitForm
            boardId={boardId}
            detail={detail}
            currentUserSub={user?.sub ?? null}
            variant="internal"
            submitFn={submitFollowUp}
            submitLabel={t.followUpCreateButton}
            onDirtyChange={setDirty}
            onSubmitted={() => {
              showToast(t.followUpCreated, "success");
              setDirty(false);
              onCreated?.();
              onOpenChange(false);
            }}
          />
        ) : (
          <div style={{ fontSize: 13, color: "var(--ink-3)", padding: "8px 2px" }}>{t.loading}</div>
        )}
      </Dialog>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t.discardTitle}
        message={t.discardMessage}
        confirmLabel={t.discardConfirm}
        cancelLabel={t.discardCancel}
        tone="danger"
        onConfirm={discardAndClose}
      />
    </>
  );
}
